import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { FC, useState, useRef, useEffect } from 'react';
import { Package2, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MaterialForm, MaterialFormRef } from 'src/pages/Materials/MaterialForm';
import { Material, trpc } from 'src/trpc';

interface MaterialSheetProps {
  material?: Material | null;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
}

export const MaterialSheet: FC<MaterialSheetProps> = ({
  material,
  isOpen,
  onOpenChange,
  onSuccess,
  trigger,
}) => {
  const [openDrawer, setOpenDrawer] = useState(isOpen ?? false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const formRef = useRef<MaterialFormRef>(null);
  const utils = trpc.useUtils();

  // Sync internal state with external state
  useEffect(() => {
    if (isOpen !== undefined) {
      setOpenDrawer(isOpen);
    }
  }, [isOpen]);

  // Reset state when drawer opens
  useEffect(() => {
    if (openDrawer) {
      setIsFormDirty(false);
      setIsFormValid(false);
    }
  }, [openDrawer]);

  const handleOpenChange = (open: boolean) => {
    if (!open && isFormDirty) {
      setShowConfirmDialog(true);
      return;
    }

    if (!open) {
      setIsFormDirty(false);
      setIsFormValid(false);
    }

    setOpenDrawer(open);
    onOpenChange?.(open);
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    setIsFormDirty(false);
    setIsFormValid(false);
    setOpenDrawer(false);
    onOpenChange?.(false);
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  const handleSuccess = async () => {
    await utils.admin.material.getAllMaterials.invalidate();
    setOpenDrawer(false);
    onOpenChange?.(false);
    onSuccess?.();
  };

  const handleSave = async () => {
    await formRef.current?.save();
  };

  const canSave = isFormValid && isFormDirty;
  const isEditing = !!material;

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Unknown';
    return format(new Date(date), "MMM do, yyyy 'at' h:mm a");
  };

  const confirmDialog = (
    <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogDescription>
            You have unsaved changes that will be lost if you leave now. Are you sure you want to
            continue without saving?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancelClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirmClose} className="bg-red-600 hover:bg-red-700">
            Leave without saving
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const sheetContent = (
    <SheetContent className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full">
      <div className="grid h-full grid-rows-[auto_1fr_auto]">
        {/* Header */}
        <SheetHeader className="border-b bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-orange-100 p-2">
                <Package2 className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-semibold text-gray-900">
                  {isEditing ? material.name : 'Create New Material'}
                </SheetTitle>
                {isEditing && (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <span>ID: {material._id}</span>
                    <Badge variant={material.archived ? 'secondary' : 'default'}>
                      {material.archived ? 'Archived' : 'Active'}
                    </Badge>
                    {material.isRestricted && <Badge variant="destructive">Restricted</Badge>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* Content Area - Scrollable */}
        <div className="overflow-y-auto px-6 py-6">
          <MaterialForm
            ref={formRef}
            initialData={material || undefined}
            onSuccess={handleSuccess}
            onStateChange={(state) => {
              setIsFormValid(state.isValid);
              setIsFormDirty(state.isDirty);
            }}
          />
        </div>

        {/* Footer - Always Visible */}
        <SheetFooter className="border-t bg-gray-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            {isEditing && (
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                <span>
                  Last updated: {material.updatedAt ? formatDate(material.updatedAt) : 'Unknown'}
                </span>
              </div>
            )}
            <div className="flex space-x-2 ml-auto">
              <SheetClose asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  Cancel
                </Button>
              </SheetClose>
              <Button
                onClick={handleSave}
                disabled={!canSave}
                className="w-full sm:w-auto text-white bg-orange-600 hover:bg-orange-700"
                type="submit"
                form="material-form"
              >
                {isEditing ? 'Save Changes' : 'Create Material'}
              </Button>
            </div>
          </div>
        </SheetFooter>
      </div>
    </SheetContent>
  );

  return (
    <>
      <Sheet open={openDrawer} onOpenChange={handleOpenChange}>
        {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
        {sheetContent}
      </Sheet>
      {confirmDialog}
    </>
  );
};
